import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit, Signal,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { HubConnectionState } from '@microsoft/signalr';
import { filter, finalize, interval, Subscription } from 'rxjs';
import { ActionsModalComponent } from '../../../../features/actions/modals/actions-modal/actions-modal.component';
import { SeatStatuses } from '../../../../features/auditorium/core/enums/SeatStatuses';
import { ISeat } from '../../../../features/auditorium/core/interfaces/ISeat';
import { SeatsPlanService } from '../../../../features/auditorium/core/services/SeatsPlanService';
import { ShapesService } from '../../../../features/auditorium/core/services/ShapesService';
import {
  CardCodeInputModalComponent
} from '../../../../features/cards/modals/card-code-input-modal/card-code-input-modal.component';
import {
  CardDetailsModalComponent
} from '../../../../features/cards/modals/card-details-modal/card-details-modal.component';
import {
  CinecardActionsModalComponent
} from '../../../../features/cards/modals/cinecard-actions-modal/cinecard-actions-modal.component';
import { SidebarCartComponent } from '../../../../features/cart/components/sidebar-cart/sidebar-cart.component';
import { CartItemTypes } from '../../../../features/cart/core/enums/CartItemTypes';
import { CartStatuses } from '../../../../features/cart/core/enums/CartStatuses';
import { ICartItem } from '../../../../features/cart/core/interfaces/ICartItem';
import { CartService } from '../../../../features/cart/core/services/CartService';
import { CartStore } from '../../../../features/cart/core/stores/CartStore';
import { PaySeparatelyStore } from '../../../../features/cart/core/stores/PaySeparatelyStore';
import { CinemaWsService } from '../../../../features/cinemas/core/services/CinemaWsService';
import { CxEntryModalComponent } from '../../../../features/cx-entry/modals/cx-entry-modal/cx-entry-modal.component';
import { OtsStaffModalComponent } from '../../../../features/ots-staff/modals/ots-staff-modal/ots-staff-modal.component';
import { ExtrasModalComponent } from '../../../../features/extras/modals/extras-modal/extras-modal.component';
import { IKioskPackage } from '../../../../features/kiosk/core/interfaces/IKioskPackage';
import { IKioskProduct } from '../../../../features/kiosk/core/interfaces/IKioskProduct';
import { KioskService } from '../../../../features/kiosk/core/services/KioskService';
import { KioskStore } from '../../../../features/kiosk/core/stores/KioskStore';
import {
  ConvertProductsToPackageModalComponent
} from '../../../../features/kiosk/modals/convert-products-to-package-modal/convert-products-to-package-modal.component';
import {
  ProductPricePickerModalComponent
} from '../../../../features/kiosk/modals/product-price-picker-modal/product-price-picker-modal.component';
import {
  TicketPackageSelectionModalComponent
} from '../../../../features/kiosk/modals/ticket-package-selection-modal/ticket-package-selection-modal.component';
import { ProductsService } from '../../../../features/products/core/services/ProductsService';
import { ReservationsStore } from '../../../../features/reservations/core/stores/ReservationsStore';
import {
  SafeActionsModalComponent
} from '../../../../features/safes/modals/safe-actions-modal/safe-actions-modal.component';
import {
  IShowPriceCategory,
  IShowPriceCategoryPackage
} from '../../../../features/shows/core/interfaces/IShowPriceCategory';
import { ShowsStore } from '../../../../features/shows/core/stores/ShowsStore';
import { SearchShowModalComponent } from '../../../../features/shows/modals/search-show-modal/search-show-modal.component';
import { ShowMovedModalComponent } from '../../../../features/shows/modals/show-moved-modal/show-moved-modal.component';
import { ITerminalPriceCategory } from '../../../../features/terminals/core/interfaces/ITerminalPriceCategory';
import { TerminalWsService } from '../../../../features/terminals/core/services/TerminalWsService';
import { IVoucherType } from '../../../../features/vouchers/core/interfaces/IVoucherType';
import { VouchersService } from '../../../../features/vouchers/core/services/VouchersService';
import {
  VoucherActionsModalComponent
} from '../../../../features/vouchers/modals/voucher-actions-modal/voucher-actions-modal.component';
import {
  VoucherAmountPickerModalComponent
} from '../../../../features/vouchers/modals/voucher-amount-picker-modal/voucher-amount-picker-modal.component';
import {
  VoucherCodeInputModalComponent
} from '../../../../features/vouchers/modals/voucher-code-input-modal/voucher-code-input-modal.component';
import { IWorkerConnectionStatus } from '../../../../features/worker/core/interfaces/IWorkerConnectionStatus';
import { AlertModalComponent } from '../../../../shared/components/alert-modal/alert-modal.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { LangUrlPipe } from '../../../../shared/pipes/lang-url.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { ApiErrorResponse } from '../../../classes/ApiErrorResponse';
import { BroadcastChannelMessageTypes } from '../../../enums/BroadcastChannelMessageTypes';
import { ISeatSelection } from '../../../interfaces/ISeatSelection';
import { BackgroundActionsService } from '../../../services/BackgroundActionsService';
import { BaseWsService } from '../../../services/BaseWsService';
import { BroadcastChannelService } from '../../../services/BroadcastChannelService';
import { ScannerService } from '../../../services/ScannerService';
import {
  OnlineOrderCodeInputModalComponent
} from '../../../../features/online-orders/modals/online-order-code-input-modal/online-order-code-input-modal.component';
import { AppStore } from '../../../stores/AppStore';
import { AddItemsToCart, RemoveItemsFromCart } from '../../../types';
import { PackageUtils } from '../../../utils/PackageUtils';
import { BaseLayoutComponent } from '../../components/abstracts/base-layout.component';
import { WebCashPermissionControlDirective } from '../../../../features/auth/core/directives/web-cash-permission-control.directive';
import { CardPaymentBalanceInfoModalComponent } from '../../../../features/cart/modals/card-payment-balance-info-modal/card-payment-balance-info-modal.component';
import { CardAmountPickerModalComponent } from '../../../../features/cards/modals/card-amount-picker-modal/card-amount-picker-modal.component';
import {
  CustomerDetailsModalComponent
} from '../../../../features/customers/modals/customer-details-modal/customer-details-modal.component';
import {
  CustomersSearchModalComponent
} from '../../../../features/customers/modals/customers-search-modal/customers-search-modal.component';
import { ICustomer } from '../../../../features/customers/core/interfaces/ICustomer';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { TOOLTIP_DELAY } from '../../../../shared/constants/tooltip';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarCartComponent,
    ContentBoxComponent,
    RouterLink,
    RouterLinkActive,
    LangUrlPipe,
    LoaderComponent,
    TicketPackageSelectionModalComponent,
    CardCodeInputModalComponent,
    CardDetailsModalComponent,
    CinecardActionsModalComponent,
    ActionsModalComponent,
    ExtrasModalComponent,
    CxEntryModalComponent,
    OtsStaffModalComponent,
    ConvertProductsToPackageModalComponent,
    ProductPricePickerModalComponent,
    TranslatePipe,
    SafeActionsModalComponent,
    VoucherActionsModalComponent,
    SearchShowModalComponent,
    ShowMovedModalComponent,
    AlertComponent,
    ModalComponent,
    VoucherAmountPickerModalComponent,
    VoucherCodeInputModalComponent,
    CardPaymentBalanceInfoModalComponent,
    CardAmountPickerModalComponent,
    CustomersSearchModalComponent,
    CustomerDetailsModalComponent,
    WebCashPermissionControlDirective,
    TooltipDirective
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'd-contents'
  }
})
export class MainLayoutComponent extends BaseLayoutComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('convertProductsToPackageModalComponent') convertProductsToPackageModalComponent: ConvertProductsToPackageModalComponent;
  @ViewChild('productPricePickerModalComponent') productPricePickerModalComponent: ProductPricePickerModalComponent;
  @ViewChild('voucherActionsModalComponent') voucherActionsModalComponent: VoucherActionsModalComponent;
  @ViewChild('scannedNumberResultsModal') scannedNumberResultsModal: ModalComponent;
  @ViewChild('voucherAmountPickerModalComponent') voucherAmountPickerModalComponent: VoucherAmountPickerModalComponent;
  @ViewChild('voucherCodeInputModalComponent') voucherCodeInputModalComponent: VoucherCodeInputModalComponent;
  @ViewChild('cardCodeInputModalComponent') cardCodeInputModalComponent: CardCodeInputModalComponent;
  @ViewChild('cardDetailsModalComponent') cardDetailsModalComponent: CardDetailsModalComponent;
  @ViewChild('showMovedModalComponent') showMovedModalComponent: ShowMovedModalComponent;
  @ViewChild('customersSearchModalComponent') customersSearchModalComponent: CustomersSearchModalComponent;
  @ViewChild('customerDetailsModalComponent') customerDetailsModalComponent: CustomerDetailsModalComponent;
  @ViewChild('cxEntryModalComponent') cxEntryModalComponent: CxEntryModalComponent;
  @ViewChild('otsStaffModalComponent') otsStaffModalComponent: OtsStaffModalComponent;

  protected readonly tooltipDelay = TOOLTIP_DELAY;

  public static instance: MainLayoutComponent;
  public scannedValResults: { voucherNr: string; cardNr: string; product: IKioskProduct; } = null;
  public getProductsByNumberSub: Subscription;
  public readonly navLinks = [
    {
      icon: 'ri-dashboard-line',
      link: '/dashboard',
      tooltipText: 'Dashboard',
      tooltipKey: 'app/nav/dashboard.Tooltip'
    },
    {
      icon: 'ri-slideshow-4-line',
      link: '/shows',
      tooltipText: 'Tickets / Kiosk',
      tooltipKey: 'app/nav/tickets-kiosk.Tooltip'
    }
  ];
  public readonly isAnyWsConnectionReconnecting = signal(false);
  public readonly isScanning = signal(false);
  public readonly isCustomerScreenOnline = signal(false);
  public readonly workerConnectionStatus = signal<'green' | 'red' | 'gray'>('gray');
  public readonly workerStatusColorClass = computed(() => {
    switch (this.workerConnectionStatus()) {
      case 'green':
        return 'c-bg-ok-500';
      case 'red':
        return 'c-bg-nok-500';
      case 'gray':
        return 'c-bg-gray-500';
      default:
        return 'c-bg-gray-500';
    }
  }) as Signal<string>;
  public readonly isCartVisible = signal(true);
  public readonly isNavVisible = signal(true);
  public readonly isCartLoading = signal(true);
  public readonly cartStore = inject(CartStore);

  private _lastPingTimestamp: Date | null = null;
  private _statusCheckInterval: any;
  private readonly _cinemaWsService = inject(CinemaWsService);
  private readonly _terminalWsService = inject(TerminalWsService);
  private readonly _cartService = inject(CartService);
  private readonly _scannerService = inject(ScannerService);
  private readonly _seatsPlanService = inject(SeatsPlanService);
  private readonly _showsStore = inject(ShowsStore);
  private readonly _reservationsStore = inject(ReservationsStore);
  private readonly _shapesService = inject(ShapesService);
  private readonly _backgroundActionsService = inject(BackgroundActionsService);
  private readonly _kioskStore = inject(KioskStore);
  private readonly _kioskService = inject(KioskService);
  private readonly _router = inject(Router);
  private readonly _broadcastChannelService = inject(BroadcastChannelService);
  private readonly _productsService = inject(ProductsService);
  private readonly _vouchersService = inject(VouchersService);
  private readonly _paySeparatelyStore = inject(PaySeparatelyStore);

  public constructor() {
    super();

    MainLayoutComponent.instance = this;

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        if (!this._showsStore.selectedShow() && !this.isCustomerScreen) {
          const hasItemsInCart = this.cartStore.cart()?.items.length > 0;

          if (!hasItemsInCart) {
            this._broadcastChannelService.cashChannelPostMessage({
              type: BroadcastChannelMessageTypes.BLANK_SCREEN,
              data: {}
            });
          }
        }
      });

    toObservable(this.cartStore.customerCards)
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(cards => {
        const lastCard = cards.at(-1);

        if (lastCard && !this.cartStore.cardIdsWithFreeEntries().includes(lastCard.cardId)) {
          if (
            lastCard.freeEntries > 0 &&
            this.cartStore.cartItemsTickets().length
          ) {
            this.cartStore.cardIdsWithFreeEntries.update(ids => [...ids, lastCard.cardId]);

            if (!this.isCustomerScreen) {
              AlertModalComponent.openAlertModal(
                'Free Entry'.trans('cards/free-entries/free-entries-info-alert.Title'),
                '<b>[[count]]</b> free entires are available.'
                  .trans(
                    'cards/free-entries/free-entries-info-alert.Text',
                    [['count', lastCard.freeEntries]]
                  ),
                this.destroyRef,
              );
            }
          }
        }
      });
  }

  public ngAfterViewInit(): void {
    this._showsStore.showMovedModalOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showMovedModalComponent?.open());
  }

  public ngOnInit(): void {
    this.loadCart();
    this._cinemaWsService.start();
    this._terminalWsService.start();
    this._backgroundActionsService.updateShowVisibilityFlagsAction.start();
    this._backgroundActionsService.showsStatisticsAction.start();
    this.subscribeToWorkerStatus();

    this.startStatusCheckInterval();
    this.startWsStatusCheckInterval();

    this._paySeparatelyStore.selectedPositionsChanged$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this._broadcastChannelService.cashChannelPostMessage({
        type: BroadcastChannelMessageTypes.PAY_SEPARATELY_SELECTION_CHANGED,
        data: {
          selectedPositions: this._paySeparatelyStore.getSelectedPositions() ?? [],
          isPaySeparatelyModeEnabled: this._paySeparatelyStore.isPaySeparatelyModeEnabled()
        }
      });
    });
  }

  public addProductToCart(item: IKioskProduct): void {
    if (item.flagVariablePrice === true) {
      this.productPricePickerModalComponent.open(item);
      return;
    }

    const productsIds = this.cartStore.cartItemsProductsAndPackages()
      .filter(p => p.itemType === CartItemTypes.Product)
      .flatMap(p => Array(p.quantity).fill(p.itemId))
      .concat(item.id)
      .sort();

    let fPackage: IKioskPackage;
    let fGroups: { groupingNr: number; products: IKioskProduct[]; }[];

    this._kioskStore.kioskProductsAndPackages().package
      .filter(p => p.packageContent.length > 0 && p.position)
      .forEach(p => {
        const groups: { groupingNr: number, products: IKioskProduct[]; }[] = [];

        p.packageContent.forEach(x => {
          const fGroup = groups
            .find(y => y.groupingNr === x.groupingNr);

          if (x.product) {
            if (fGroup) {
              fGroup.products.push(x.product);
            } else {
              groups.push({
                groupingNr: x.groupingNr,
                products: [x.product]
              });
            }
          }
        });

        const includesAllWithCounts =
          (source: number[], target: number[]): boolean => {
            const tmp = [...source];

            return target.every(id => {
              const idx = tmp.indexOf(id);

              if (idx === -1) {
                return false;
              }

              tmp.splice(idx, 1);

              return true;
            });
          };

        if (
          groups.every(x => x.products.length === 1) ||
          (groups.length === 1 && groups[0].groupingNr === undefined)
        ) {
          if (
            includesAllWithCounts(
              productsIds,
              groups.flatMap(g => g.products.map(p => p.id))
            )
          ) {
            fPackage = p;
            fGroups = groups;
          }
        } else {
          const groupsWithoutGroupingNrProductsIds = groups
            .filter(g => g.groupingNr === undefined)
            .flatMap(g => g.products)
            .map(p => p.id)
            .sort();

          const checkGroupsWithGroupingNr = () => {
            const getCombinations =
              (groups: { groupingNr: number; products: IKioskProduct[]; }[]): number[][] => {
                return groups.reduce(
                  (acc: number[][], group) => {
                    const products = group.products.map(p => p.id);

                    return acc.length === 0
                      ? products.map(p => [p])
                      : acc.flatMap(combo => products.map(p => [...combo, p]));
                  },
                  []
                );
              };

            if (
              getCombinations(groups)
                .filter(combo => includesAllWithCounts(productsIds, combo))
                .length > 0
            ) {
              fPackage = p;
              fGroups = groups;
            }
          };

          if (groupsWithoutGroupingNrProductsIds.length > 0) {
            if (groupsWithoutGroupingNrProductsIds.every(id => productsIds.includes(id))) {
              checkGroupsWithGroupingNr();
            }
          } else {
            checkGroupsWithGroupingNr();
          }
        }
      });

    if (fPackage && !this._kioskStore.rejectedPackagesToConvertIds().includes(fPackage.id)) {
      this.convertProductsToPackageModalComponent.open(
        {
          convertProductsToPackageData: {
            item,
            package: fPackage,
            productsIds,
            groups: fGroups
          }
        }
      );
    } else {
      const itemToAdd: Partial<ICartItem> = {
        itemId: item.id,
        itemType: CartItemTypes.Product,
        quantity: 1
      };

      this._cartService.addConcessionItemToCart(itemToAdd).subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-product-item-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
    }
  }

  public scannedVoucherActions(): void {
    this.voucherCodeInputModalComponent.open(null, null, this.scannedValResults.voucherNr);
  }

  public scannedCardActions(): void {
    if (this.cartStore.customerCards().length > 0) {
      this.cardDetailsModalComponent.open({ card: this.cartStore.customerCards()[0] });
    } else {
      this.cardCodeInputModalComponent.open(
        null,
        'add-payment-method',
        this.scannedValResults.cardNr
      );
    }
  }

  public restoreTerminalCinema(): void {
    this.terminalsStore.changedCinema.set(null);
    this._cartService.clearCart();
    AppStore.shouldPreventAppReload.set(false);
    location.href = '/'.langUrl();
  }

  public openCustomerAccountsSearch(): void {
    this.customersSearchModalComponent.open({ skipCartCustomerPreload: true });
  }

  public onCustomerAccountSelected(customer: ICustomer): void {
    if (!customer?.id) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'Customer not found.'.trans('get-customer-by-id/notifications/CustomerNotFound.Message')
      );
      return;
    }

    this.customerDetailsModalComponent.open({ customerId: customer.id });
  }

  private subscribeToWorkerStatus(): void {
    this._terminalWsService.onWorkerStatusChanged(payload => {
      if (payload.result) {
        this.updateWorkerStatus(payload.result);
      }
    });
  }

  private updateWorkerStatus(status: IWorkerConnectionStatus): void {
    this._lastPingTimestamp = new Date();
    this.checkStatusBasedOnTime();
  }

  private startStatusCheckInterval(): void {
    this._statusCheckInterval = setInterval(() => {
      this.checkStatusBasedOnTime();
    }, 1000);
  }

  private checkStatusBasedOnTime(): void {
    if (!this._lastPingTimestamp) {
      this.workerConnectionStatus.set('gray');

      return;
    }

    const now = new Date();
    const timeSinceLastPing = (now.getTime() - this._lastPingTimestamp.getTime()) / 1000;
    this.workerConnectionStatus.set(timeSinceLastPing >= 5 ? 'red' : 'green');
  }

  public onTicketPackageSelectionModalAbort(seatSelections: ISeatSelection[]): void {
    const seats = this._seatsPlanService.seats();
    const optimisticSeatIds = seatSelections
      .map(selection => seats.find(seat => seat.seatId === selection.id)?.id)
      .filter((seatId): seatId is number => seatId !== undefined);

    this._seatsPlanService.revertOptimisticPriceRowSeats(optimisticSeatIds);

    const seatsAfterRevert = this._seatsPlanService.seats();

    seatSelections.forEach(s => {
      const fSeat = seatsAfterRevert.find(y => y.seatId === s.id);

      if (!fSeat) {
        return;
      }

      const fShapeComponent = this._shapesService.getShapeComponent(fSeat.seatId);
      fSeat.isMy = false;

      if (fSeat.seatStatusKey === SeatStatuses.NOTED) {
        fSeat.seatStatusKey = SeatStatuses.FREE;
      }

      fShapeComponent.dataObject = {
        ...fShapeComponent.dataObject,
        isMy: fSeat.isMy,
        seatStatusKey: fSeat.seatStatusKey
      };
    });

    this._seatsPlanService.setSeats(seatsAfterRevert);
  }

  private loadCart(): void {
    if (this.terminalsStore.selectedTerminal().currentSession.currentBasketId) {
      this._cartService.getCart().pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isCartLoading.set(false))
      ).subscribe({
        next: () => this.afterCartLoaded(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`get-cart/notifications/${error.message}.Message`)
          );
        }
      });
    } else {
      this._cartService.createCart(false).pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isCartLoading.set(false))
      ).subscribe({
        next: () => this.afterCartLoaded(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`create-cart/notifications/${error.message}.Message`)
          );
        }
      });
    }
  }

  private afterCartLoaded(): void {
    const cartStatus = this.cartStore.cart().status;
    if (cartStatus === CartStatuses.CheckedOut || cartStatus === CartStatuses.CheckoutFailed) {
      SidebarCartComponent.instance.summaryModalComponent.open();
    }

    if (!this.isCustomerScreen) {
      this._scannerService.scannedVal$
        .pipe(
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(val => {
          if (this.cxEntryModalComponent?.isOpen) {
            this.cxEntryModalComponent.forwardBarcodeScan(val);
            return;
          }

          if (this.isScanning()) {
            return;
          }

          if (OnlineOrderCodeInputModalComponent.instance) {
            OnlineOrderCodeInputModalComponent.instance.code = val;
            OnlineOrderCodeInputModalComponent.instance.onSubmit();

            return;
          }

          if (VoucherCodeInputModalComponent.instance) {
            VoucherCodeInputModalComponent.instance.code = val;
            VoucherCodeInputModalComponent.instance.onSubmit();

            return;
          }

          if (CardCodeInputModalComponent.instance) {
            CardCodeInputModalComponent.instance.code = val;
            CardCodeInputModalComponent.instance.onSubmit();

            return;
          }

          this.scannedValResults = {
            product: this._kioskStore.kioskProductsAndPackages().products.find(p => p.eanCode === val),
            voucherNr: null,
            cardNr: null
          };

          this.isScanning.set(true);

          this._productsService.getProductsByNumber(val)
            .pipe(
              finalize(() => this.isScanning.set(false))
            )
            .subscribe({
              next: res => {
                this.scannedValResults.voucherNr = res.data.voucherNr;
                this.scannedValResults.cardNr = res.data.cardNr;

                if (this.scannedValResults.voucherNr || this.scannedValResults.cardNr || this.scannedValResults.product) {
                  if (!this.scannedValResults.voucherNr && !this.scannedValResults.cardNr && this.scannedValResults.product) {
                    this.addProductToCart(this.scannedValResults.product);

                    return;
                  }

                  if (this.scannedValResults.voucherNr && !this.scannedValResults.cardNr && !this.scannedValResults.product) {
                    VoucherCodeInputModalComponent.instance?.close();
                    this.scannedVoucherActions();

                    return;
                  }

                  if (!this.scannedValResults.voucherNr && this.scannedValResults.cardNr && !this.scannedValResults.product) {
                    CardCodeInputModalComponent.instance?.close();
                    this.scannedCardActions();

                    return;
                  }

                  this.scannedNumberResultsModal.open();
                }
              },
              error: err => {
                if (this.scannedValResults.product) {
                  this.addProductToCart(this.scannedValResults.product);
                }
              }
            });
        });
    }

    this._seatsPlanService.seatsToAdd$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(val => this.handleSeatSelection(val));

    this._seatsPlanService.seatsToRemove$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(val => this.handleSeatsToRemove(val));

    this._seatsPlanService.seatsToReplace$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(val => {
        this.handleSeatsToRemove(
          val.seatsToRemove,
          () => this.handleSeatSelection(val.seatsToAdd)
        );
      });

    this._kioskService.productToAdd$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(val => this.addProductToCart(val));

    this._vouchersService.voucherToAdd$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(val => this.addVoucherToCart(val));
  }

  private addVoucherToCart(voucher: IVoucherType): void {
    if (voucher.isSeries) {
      this.voucherCodeInputModalComponent.open(voucher);
    } else if (voucher.flagVariable) {
      this.voucherAmountPickerModalComponent.open(voucher);
    } else {
      const items: AddItemsToCart = [
        {
          itemId: voucher.voucherTypeId,
          itemType: CartItemTypes.Voucher,
          quantity: 1
        }
      ];

      this._cartService.addItemToCart(items).subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-voucher-item-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
    }
  }

  private handleSeatSelection(seats: ISeat[]): void {
    const terminalPriceCategories = this.terminalsStore.selectedTerminal().priceCategories;

    const handleMatchedPriceCategory =
      (priceCategory: IShowPriceCategory): void => {
        seats.forEach(s => (s.priceCategoryId = priceCategory.id));

        if (priceCategory.packages.length > 0) {
          const seatSelections = seats.map(x => ({
            rankToken: x.rankToken,
            seatNumber: x.seatNumber,
            priceAreaId: x.priceAreaId,
            priceCategoryId: x.priceCategoryId,
            id: x.seatId
          }));

          const kioskProductsAndPackages = this._kioskStore.kioskProductsAndPackages();

          if (PackageUtils.canAutoSelectPackagesForAllSeats(seatSelections, priceCategory, kioskProductsAndPackages)) {
            this.addItemToCartWithPackage(
              PackageUtils.getAutoSelectedPackages(
                seatSelections,
                priceCategory,
                kioskProductsAndPackages
              ),
              seats
            );

            return;
          }

          TicketPackageSelectionModalComponent.instance.open(
            seatSelections,
            priceCategory,
            packageSeatSelections => this.addItemToCartWithPackage(packageSeatSelections, seats)
          );
        } else {
          this.addSeatToCart(seats);
        }
      };

    const checkTerminalPriceCategories =
      (priceCategory: ITerminalPriceCategory): boolean => {
        if (!priceCategory) {
          return false;
        }

        const spc = this._showsStore.showPriceCategories()
          .find(spc => spc.id === priceCategory.priceCategoryId);

        if (!spc) {
          return false;
        }

        handleMatchedPriceCategory(spc);

        return true;
      };

    if (this._reservationsStore.rebookReservation()) {
      const reservationPriceCategory = this._showsStore.showReservationPriceCategory();

      if (reservationPriceCategory) {
        handleMatchedPriceCategory(reservationPriceCategory);
        return;
      }
    }

    if (
      checkTerminalPriceCategories(
        terminalPriceCategories.find(tpc => tpc.priceCategoryId === seats[0].priceCategoryId)
      )
    ) {
      return;
    }

    if (checkTerminalPriceCategories(terminalPriceCategories.find(tpc => tpc.flagDefault))) {
      return;
    }

    const fallbackPriceCat = this._showsStore.showPriceCategories()
      .find(
        spc => terminalPriceCategories.some(tpc => tpc.priceCategoryId === spc.id)
      );

    if (fallbackPriceCat) {
      handleMatchedPriceCategory(fallbackPriceCat);
    } else {
      this._seatsPlanService.revertOptimisticPriceRowSeats(seats.map(seat => seat.id));
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'No price category found.'.trans('notifications/no-price-category-found.Message')
      );
    }
  }

  private addSeatToCart(seats: ISeat[]): void {
    const items: AddItemsToCart = seats.map(x => {
      return {
        itemId: x.seatId,
        quantity: 1,
        itemType: CartItemTypes.Ticket,
        priceCategoryId: x.priceCategoryId
      };
    });

    this._cartService.addItemToCart(items)
      .subscribe({
        next: () => {
          this._seatsPlanService.acknowledgeOptimisticPriceRowSeats(seats.map(seat => seat.id));
        },
        error: ({ error }: ApiErrorResponse) => {
          this._seatsPlanService.revertOptimisticPriceRowSeats(seats.map(seat => seat.id));
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-ticket-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private addItemToCartWithPackage(
    seatSelections: { seat: ISeatSelection; package: IShowPriceCategoryPackage; selectedProductIds: number[]; }[],
    sourceSeats: ISeat[]
  ): void {
    const items: AddItemsToCart = seatSelections.map(x => {
      return {
        itemId: x.seat.id,
        quantity: 1,
        itemType: CartItemTypes.Ticket,
        packageId: x.package.id,
        packageProductIds: x.selectedProductIds,
        priceCategoryId: x.seat.priceCategoryId
      };
    });

    const optimisticSeatIds = sourceSeats.map(seat => seat.id);

    this._cartService.addItemToCart(items).subscribe({
      next: () => {
        this._seatsPlanService.acknowledgeOptimisticPriceRowSeats(optimisticSeatIds);
      },
      error: ({ error }: ApiErrorResponse) => {
        this._seatsPlanService.revertOptimisticPriceRowSeats(optimisticSeatIds);
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`add-ticket-to-cart/notifications/${error.message}.Message`)
        );
      }
    });
  }

  private startWsStatusCheckInterval(): void {
    interval(5000)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() =>{
        this.isAnyWsConnectionReconnecting.set(
          BaseWsService.connections
            .some(c => c.getConnectionState() === HubConnectionState.Reconnecting)
        );
      });
  }

  private handleSeatsToRemove(seats: ISeat[], onSuccess?: () => void): void {
    const itemsToDelete: RemoveItemsFromCart = seats
      .map(s => {
        const fCartItem = this.cartStore.cart().items
          .find(item => item.itemId === s.id && item.itemType === CartItemTypes.Ticket);

        return {
          itemId: fCartItem?.itemId,
          position: fCartItem?.position
        };
      })
      .filter(i => i.itemId && i.position);

    this._cartService.removeItemFromCart(itemsToDelete).subscribe({
      next: () => {
        if (onSuccess) {
          onSuccess();
        }
      },
      error: ({ error }: ApiErrorResponse) => {
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`remove-item-from-cart/notifications/${error.message}.Message`)
        );
      }
    });
  }

  public ngOnDestroy(): void {
    if (this._statusCheckInterval) {
      clearInterval(this._statusCheckInterval);
    }

    this._backgroundActionsService.showsStatisticsAction.stop();
    this._backgroundActionsService.updateShowVisibilityFlagsAction.stop();
  }
}
